// =============================================================================
// FILE:
// frontend/src/pms/task_execution/drawers/RequestDetailDrawer.js
// =============================================================================

import React, {
  useEffect,
  useState
} from "react";

import {
  X,
  Maximize2,
  Minimize2
} from "lucide-react";

import {
  getMaterialRequestDetails,

  approveRequestLine,

  rejectRequestLine,
  issueAgainstRequestLine
} from "../services/taskMaterialRequestService";


// =============================================================================
// COMPONENT
// =============================================================================

export default function RequestDetailDrawer({

  open,

  requestId,

  onClose

}) {


  // ===========================================================================
  // STATE
  // ===========================================================================
  const [loading, setLoading] =
    useState(false);


  const [isFullscreen, setIsFullscreen] =
    useState(false);


  const [requestData, setRequestData] =
    useState(null);

    const [approvalQty, setApprovalQty] =
  useState({});


const [lineRemarks, setLineRemarks] =
  useState({});
const [issueQty, setIssueQty] =
  useState({});


const [issueRemarks, setIssueRemarks] =
  useState({});



  // ===========================================================================
  // LOAD
  // ===========================================================================
  useEffect(() => {

    if (
      open &&
      requestId
    ) {

      loadRequestDetails();
    }

  }, [open, requestId]);


  // ===========================================================================
  // LOAD DETAILS
  // ===========================================================================
  const loadRequestDetails =
    async () => {

      try {

        setLoading(true);

        // ---------------------------------------------------------------
        // TODO:
        // Replace mock with backend API
        // ---------------------------------------------------------------

        const response =
          await getMaterialRequestDetails(
            requestId
          );

        if (response?.success) {

  const raw =
    response.data || {};


  const header =
    raw.header || {};


  setRequestData({

    request_no:
      header.request_no,

    request_status:
      header.request_status,

    request_date:
      header.request_date,

    requested_by:
      header.assigned_user_name ||

      header.requested_by ||

      "-",

    remarks:
      header.remarks,

    lines:
      raw.lines || []
  });

}
        
          else {

          // -----------------------------------------------------------
          // TEMP MOCK
          // -----------------------------------------------------------
          setRequestData({

            request_no:
              "MRQ-20260523001",

            request_status:
              "APPROVED",

            request_date:
              "2026-05-23",

            requested_by:
              "Site Engineer",

            remarks:
              "Urgent piping request",

            lines: [

              {
                id: 1,

                material_code:
                  "RM-PIPE-001",

                material_description:
                  "CS PIPE SCH40",

                requested_qty:
                  25,

                approved_qty:
                  25,

                issued_qty:
                  10,

                line_status:
                  "PARTIALLY_ISSUED"
              },

              {
                id: 2,

                material_code:
                  "RM-FLANGE-002",

                material_description:
                  "WNRF FLANGE",

                requested_qty:
                  5,

                approved_qty:
                  5,

                issued_qty:
                  5,

                line_status:
                  "ISSUED"
              }
            ]
          });
        }

      } catch (err) {

        console.error(
          "Failed to load request details",
          err
        );

      } finally {

        setLoading(false);
      }
    };


  
    // ===========================================================================
  // APPROVE LINE
  // ===========================================================================
  const handleApproveLine =
    async (line) => {

      try {

        await approveRequestLine(

          line.id,

          {

            approved_qty:

              approvalQty[line.id]

              ||

              line.requested_qty,

            remarks:

              lineRemarks[line.id]

              || ""
          }
        );


        // -------------------------------------------------------------
        // RELOAD
        // -------------------------------------------------------------
        loadRequestDetails();

      } catch (err) {

        console.error(
          "Failed to approve line",
          err
        );
      }
    };


  // ===========================================================================
  // REJECT LINE
  // ===========================================================================
  const handleRejectLine =
    async (line) => {

      try {

        await rejectRequestLine(

          line.id,

          {

            remarks:

              lineRemarks[line.id]

              || ""
          }
        );


        // -------------------------------------------------------------
        // RELOAD
        // -------------------------------------------------------------
        loadRequestDetails();

      } catch (err) {

        console.error(
          "Failed to reject line",
          err
        );
      }
    };
    
      // ===========================================================================
  // ISSUE MATERIAL
  // ===========================================================================
  const handleIssueLine =
    async (line) => {

      try {

        await issueAgainstRequestLine(

          line.id,

          {

            issue_qty:

              issueQty[line.id]

              ||

              (
                line.approved_qty
                -
                (
                  line.issued_against_request_qty
                  || 0
                )
              ),

            issue_date:
              new Date()
              .toISOString()
              .slice(0, 10),

            remarks:

              issueRemarks[line.id]

              || ""
          }
        );


        // -------------------------------------------------------------
        // RELOAD
        // -------------------------------------------------------------
        loadRequestDetails();

      } catch (err) {

        console.error(
          "Failed to issue material",
          err
        );
      }
    };

    // ===========================================================================
  // CLOSE
  // ===========================================================================
  if (!open) {
    return null;
  }


  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (

    <div style={styles.overlay}>


      <div
        style={{
          ...styles.drawer,

          width:
            isFullscreen
              ? "100%"
              : "72%"
        }}
      >


        {/* ============================================================ */}
        {/* HEADER */}
        {/* ============================================================ */}
        <div style={styles.header}>


          <div>

            <div style={styles.title}>

              Request Details

            </div>


            <div style={styles.subtitle}>


              {

                requestData?.request_no ||

                "Loading..."
              }

            </div>

          </div>


          <div style={styles.headerActions}>


            <button
              style={styles.iconBtn}
              onClick={() =>
                setIsFullscreen(
                  !isFullscreen
                )
              }
            >

              {

                isFullscreen

                  ?

                  <Minimize2 size={18} />

                  :

                  <Maximize2 size={18} />
              }

            </button>


            <button
              style={styles.iconBtn}
              onClick={onClose}
            >

              <X size={18} />

            </button>

          </div>

        </div>


        {/* ============================================================ */}
        {/* BODY */}
        {/* ============================================================ */}
        <div style={styles.body}>


          {

            loading

              ?

              (

                <div style={styles.loader}>

                  Loading request details...

                </div>
              )

              :

              (

                <>


                  {/* ================================================= */}
                  {/* SUMMARY */}
                  {/* ================================================= */}
                  <div style={styles.summaryGrid}>


                    <SummaryCard
                      label="Request Status"
                      value={
                        requestData?.request_status
                      }
                    />


                    <SummaryCard
                      label="Request Date"
                      value={
                        requestData?.request_date
                      }
                    />


                    <SummaryCard
                      label="Requested By"
                      value={
                        requestData?.requested_by
                      }
                    />


                    <SummaryCard
                      label="Total Lines"
                      value={
                        requestData?.lines?.length || 0
                      }
                    />

                  </div>


                  {/* ================================================= */}
                  {/* REMARKS */}
                  {/* ================================================= */}
                  <div style={styles.card}>


                    <div style={styles.cardTitle}>

                      Remarks

                    </div>


                    <div style={styles.remarksText}>


                      {

                        requestData?.remarks ||

                        "-"
                      }

                    </div>

                  </div>


                  {/* ================================================= */}
                  {/* LINES */}
                  {/* ================================================= */}
                  <div style={styles.card}>


                    <div style={styles.cardTitle}>

                      Request Lines

                    </div>


                    <div style={styles.gridHeader}>


                      <div style={styles.colMaterial}>
                        Material
                      </div>


                      <div style={styles.colQty}>
                        Requested
                      </div>


                      <div style={styles.colQty}>
                        Approved
                      </div>


                      <div style={styles.colQty}>
                        Issued
                      </div>


                      <div style={styles.colStatus}>
                        Status
                      </div>
                      <div style={styles.colAction}>
                        Action
                      </div>

                    </div>


                    {

                      requestData?.lines?.map((line) => (

                        <div
                          key={line.id}
                          style={styles.gridRow}
                        >


                          <div style={styles.colMaterial}>


                            <div style={styles.materialCode}>


                              {
                                line.material_code ||

                                line.material_item_code ||

                                line.item_code ||

                                "-"
                              }

                            </div>


                            <div style={styles.materialDesc}>


                              {
                                line.material_description ||

                                line.material_name ||

                                line.material ||

                                "-"
                              }

                            </div>

                          </div>


                          <QtyCell
                            value={
                              line.requested_qty
                            }
                          />


                          <QtyCell
                            value={
                              line.approved_qty
                            }
                          />


                          <QtyCell
                            value={
                              line.issued_qty
                            }
                          />


                          <div style={styles.colStatus}>


                            <span style={
                              getStatusStyle(
                                line.line_status
                              )
                            }>

                              {
                                formatStatus(
                                  line.line_status
                                )
                              }

                            </span>

                          </div>
                          <div style={styles.colAction}>


  {

    requestData?.request_status ===
    "SUBMITTED"

    &&

    line.line_status ===
    "PENDING"

    && (

      <div style={styles.actionContainer}>


        {/* -------------------------------------------------------- */}
        {/* APPROVAL QTY */}
        {/* -------------------------------------------------------- */}
        <input

          type="number"

          value={
            approvalQty[line.id]

            ||

            line.requested_qty
          }

          onChange={(e) =>

            setApprovalQty({

              ...approvalQty,

              [line.id]:
                e.target.value
            })
          }

          style={styles.qtyInput}
        />


        {/* -------------------------------------------------------- */}
        {/* REMARKS */}
        {/* -------------------------------------------------------- */}
        <input

          type="text"

          placeholder="Remarks"

          value={
            lineRemarks[line.id]
            || ""
          }

          onChange={(e) =>

            setLineRemarks({

              ...lineRemarks,

              [line.id]:
                e.target.value
            })
          }

          style={styles.remarksInput}
        />


        {/* -------------------------------------------------------- */}
        {/* APPROVE */}
        {/* -------------------------------------------------------- */}
        <button

          style={styles.approveBtn}

          onClick={() =>
            handleApproveLine(
              line
            )
          }
        >

          Approve

        </button>


        {/* -------------------------------------------------------- */}
        {/* REJECT */}
        {/* -------------------------------------------------------- */}
        <button

          style={styles.rejectBtn}

          onClick={() =>
            handleRejectLine(
              line
            )
          }
        >

          Reject

        </button>

      </div>
    )
  }
    {

    (
      line.line_status === "APPROVED"

      ||

      line.line_status === "PARTIALLY_ISSUED"
    )

    && (

      <div style={styles.issueContainer}>


        {/* -------------------------------------------------------- */}
        {/* ISSUE QTY */}
        {/* -------------------------------------------------------- */}
        <input

          type="number"

          value={

            issueQty[line.id]

            ||

            (
              line.approved_qty
              -
              (
                line.issued_against_request_qty
                || 0
              )
            )
          }

          onChange={(e) =>

            setIssueQty({

              ...issueQty,

              [line.id]:
                e.target.value
            })
          }

          style={styles.qtyInput}
        />


        {/* -------------------------------------------------------- */}
        {/* ISSUE REMARKS */}
        {/* -------------------------------------------------------- */}
        <input

          type="text"

          placeholder="Issue Remarks"

          value={
            issueRemarks[line.id]
            || ""
          }

          onChange={(e) =>

            setIssueRemarks({

              ...issueRemarks,

              [line.id]:
                e.target.value
            })
          }

          style={styles.remarksInput}
        />


        {/* -------------------------------------------------------- */}
        {/* ISSUE BUTTON */}
        {/* -------------------------------------------------------- */}
        <button

          style={styles.issueBtn}

          onClick={() =>
            handleIssueLine(
              line
            )
          }
        >

          Issue

        </button>

      </div>
    )
  }

</div>


                        </div>
                      ))
                    }

                  </div>

                </>
              )
          }

        </div>

      </div>

    </div>
  );
}


// =============================================================================
// SUMMARY CARD
// =============================================================================

function SummaryCard({

  label,

  value

}) {

  return (

    <div style={styles.summaryCard}>


      <div style={styles.summaryLabel}>

        {label}

      </div>


      <div style={styles.summaryValue}>

        {value || "-"}

      </div>

    </div>
  );
}


// =============================================================================
// QTY CELL
// =============================================================================

function QtyCell({

  value

}) {

  return (

    <div style={styles.colQty}>


      <div style={styles.qtyValue}>


        {value || 0}

      </div>

    </div>
  );
}


// =============================================================================
// HELPERS
// =============================================================================

function formatStatus(status) {

  if (!status) {
    return "-";
  }

  return status
    .replaceAll("_", " ");
}


function getStatusStyle(status) {

  switch (status) {

    case "ISSUED":

      return styles.statusGreen;

    case "PARTIALLY_ISSUED":

      return styles.statusOrange;

    case "APPROVED":

      return styles.statusBlue;

    default:

      return styles.statusGray;
  }
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  overlay: {

    position: "fixed",

    top: 0,

    left: 0,

    right: 0,

    bottom: 0,

    background:
      "rgba(0,0,0,0.35)",

    display: "flex",

    justifyContent: "flex-end",

    zIndex: 99999
  },

  drawer: {

    height: "100vh",

    background: "#f5f7fb",

    display: "flex",

    flexDirection: "column",

    transition:
      "width 0.25s ease"
  },

  header: {

    background: "#ffffff",

    padding: 20,

    borderBottom:
      "1px solid #e5e7eb",

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center"
  },

  title: {

    fontSize: 22,

    fontWeight: 700,

    color: "#111827"
  },

  subtitle: {

    marginTop: 6,

    fontSize: 13,

    color: "#6b7280"
  },

  headerActions: {

    display: "flex",

    gap: 10
  },

  iconBtn: {

    width: 36,

    height: 36,

    borderRadius: 8,

    border: "1px solid #d1d5db",

    background: "#fff",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    cursor: "pointer"
  },

  body: {

    flex: 1,

    overflow: "auto",

    padding: 20,

    display: "flex",

    flexDirection: "column",

    gap: 20
  },

  summaryGrid: {

    display: "grid",

    gridTemplateColumns:
      "repeat(4, 1fr)",

    gap: 16
  },

  summaryCard: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 18,

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  summaryLabel: {

    fontSize: 12,

    color: "#6b7280",

    marginBottom: 8
  },

  summaryValue: {

    fontSize: 16,

    fontWeight: 700,

    color: "#111827"
  },

  card: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 20,

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  cardTitle: {

    fontSize: 16,

    fontWeight: 700,

    marginBottom: 18,

    color: "#111827"
  },

  remarksText: {

    fontSize: 14,

    lineHeight: "24px",

    color: "#374151"
  },

  gridHeader: {

    display: "flex",

    background: "#f3f4f6",

    padding: "14px 16px",

    borderRadius: 10,

    marginBottom: 10,

    fontSize: 13,

    fontWeight: 700,

    color: "#374151"
  },

  gridRow: {

    display: "flex",

    alignItems: "center",

    padding: "16px",

    borderBottom:
      "1px solid #f3f4f6"
  },

  colMaterial: {

    flex: 1
  },

  materialCode: {

    fontSize: 13,

    fontWeight: 700,

    color: "#2563eb"
  },

  materialDesc: {

    marginTop: 4,

    fontSize: 13,

    color: "#374151"
  },

  colQty: {

    width: 140,

    display: "flex",

    justifyContent: "center"
  },

  qtyValue: {

    fontSize: 14,

    fontWeight: 700,

    color: "#111827"
  },

  colStatus: {

    width: 180,

    display: "flex",

    justifyContent: "center"
  },

  statusGreen: {

    background: "#dcfce7",

    color: "#166534",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  statusOrange: {

    background: "#ffedd5",

    color: "#c2410c",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  statusBlue: {

    background: "#dbeafe",

    color: "#1d4ed8",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  statusGray: {

    background: "#e5e7eb",

    color: "#374151",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

    colAction: {

    width: 420,

    display: "flex",

    justifyContent: "center"
  },


  actionContainer: {

    display: "flex",

    alignItems: "center",

    gap: 8
  },


  qtyInput: {

    width: 80,

    height: 34,

    borderRadius: 8,

    border: "1px solid #d1d5db",

    padding: "0 10px"
  },


  remarksInput: {

    width: 140,

    height: 34,

    borderRadius: 8,

    border: "1px solid #d1d5db",

    padding: "0 10px"
  },


  approveBtn: {

    height: 34,

    border: "none",

    background: "#16a34a",

    color: "#ffffff",

    borderRadius: 8,

    padding: "0 12px",

    cursor: "pointer",

    fontWeight: 600
  },


  rejectBtn: {

    height: 34,

    border: "none",

    background: "#dc2626",

    color: "#ffffff",

    borderRadius: 8,

    padding: "0 12px",

    cursor: "pointer",

    fontWeight: 600
  },

    issueContainer: {

    display: "flex",

    alignItems: "center",

    gap: 8,

    marginTop: 8
  },


  issueBtn: {

    height: 34,

    border: "none",

    background: "#7c3aed",

    color: "#ffffff",

    borderRadius: 8,

    padding: "0 12px",

    cursor: "pointer",

    fontWeight: 600
  },
  
  loader: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 40,

    textAlign: "center"
  }
};